import { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import type { MarkerFactory, OnMapLoaded } from './PcaMap';
import type { Google } from './types';
import { getDefaultGoogleMapInitialOptions } from './config';
import { PcaMapState } from '../../../contexts/PcaMapContext/PcaMapContext';
import { useUrlContext } from '../../../contexts/UrlContext/UrlContext';

interface PcaMapConfig extends google.maps.MapOptions {
  markerFactories?: ReadonlyArray<MarkerFactory>;
  onMapLoaded?: OnMapLoaded | null;
}

const GOOGLE_MAPS_VERSION = '3.59';

export const usePcaMap = (apiKey: string, config?: PcaMapConfig): PcaMapState => {
  const { markerFactories = [], onMapLoaded, ...googleMapConfig } = config ?? {};
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [google, setGoogle] = useState<Google>();
  const [isLoading, setIsLoading] = useState(true);
  const { url } = useUrlContext();
  const googleMapLoader = new Loader({
    apiKey,
    version: GOOGLE_MAPS_VERSION,
    language: 'en-AU',
    region: 'AU',
    libraries: ['geometry'],
  });
  const googleMapConfigWithDefaults = {
    ...getDefaultGoogleMapInitialOptions(),
    ...googleMapConfig,
  };

  useEffect(() => {
    setMap(undefined);
    setIsLoading(true);
  }, [url.pathname]);

  useEffect(() => {
    const { current } = ref;
    if (!current) return undefined;
    if (!google) {
      void googleMapLoader.load().then(setGoogle);
      return undefined;
    }
    if (!map) {
      setMap(new google.maps.Map(current, googleMapConfigWithDefaults));
      return undefined;
    }
    markerFactories.forEach((markerFactory) => {
      markerFactory({ google, map });
    });

    if (onMapLoaded) onMapLoaded({ google, map });
    setIsLoading(false);
    return () => {
      google.maps.event.clearInstanceListeners(map);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.current, google, map]);

  return { ref, map, google, isLoading };
};
